"use client";

/**
 * `FieldRenderer` — central dispatcher that takes a {@link FieldDef} and
 * renders the right field component based on `field.type`.
 *
 * The `<Form>` composer (Phase J) iterates over the fields config and
 * emits one `<FieldRenderer>` per entry. Field components themselves
 * read the rhf instance from `useFormContext`, so this dispatcher does
 * not need to thread the form down — it only owns the type → component
 * mapping plus reactive conditional-visibility wiring.
 *
 * Phase B coverage: text, textarea, number, checkbox, switch, radio.
 * Phase C coverage: money, percent, date.
 * Phase D coverage: select, multiSelect, tags.
 * Phase E coverage: file.
 * Phase F coverage: repeater, relation.
 * Phase G coverage: reactive conditional visibility via `useWatch`.
 *
 * The runtime check on the trailing `default` branch is paired with a
 * compile-time exhaustiveness assertion (`never` cast) so that adding a
 * new variant to the FieldType union without a renderer fails the
 * typecheck. The runtime throw is the safety net for code paths that
 * widen the discriminator (e.g. data loaded from JSON).
 *
 * `pathPrefix` is how the {@link RepeaterField} threads its row index
 * down into nested fields. When the prefix is set, every field component
 * computes its rhf binding path as `${pathPrefix}.${field.name}` instead
 * of plain `field.name`, so a `pib` field nested in `partners[2]`
 * registers under the path `partners.2.pib`. Top-level (non-nested)
 * usage omits the prop entirely and behaves exactly as before.
 *
 * **Conditional visibility (Task 14).** If a field carries a `condition`
 * predicate, the renderer subscribes to the entire form-values map via
 * rhf's `useWatch` and re-evaluates the predicate on every value change.
 * When the predicate returns `false`, we render `null` rather than the
 * field component — so hidden fields disappear from the DOM but their
 * persisted form-state value remains intact (rhf doesn't unregister the
 * binding just because we stopped rendering it). This means a user who
 * fills the conditional field, hides it by changing a controlling value,
 * then re-shows it, gets their original input back.
 *
 * **Why `useWatch` always runs, even for fields without a condition:**
 * React's rules-of-hooks forbid conditional hook calls. We could split
 * into two components ("with condition" vs "without"), but that doubles
 * the surface for a one-line behavioural difference. Subscribing to the
 * full values map is cheap — `useWatch` returns a stable reference when
 * nothing the field cares about changes (rhf does the structural-equality
 * check internally), and the predicate runs in O(1) for the typical
 * "values.choice === 'other'" pattern. The cost is bounded by the form's
 * own re-renders.
 *
 * Defence-in-depth: individual field components also evaluate the
 * predicate against `getValues()` on first render. That second check is
 * a no-op when the field is rendered through this dispatcher (because we
 * already short-circuited), but matters when callers render a field
 * directly without going through the renderer.
 */

import * as React from "react";
import { useWatch } from "react-hook-form";
import type { FieldDef, FormValues } from "./types";
import { useFormContext } from "./form-context";
import { evaluateCondition } from "./conditional";
import { TextField } from "./fields/text-field";
import { TextareaField } from "./fields/textarea-field";
import { NumberField } from "./fields/number-field";
import { CheckboxField } from "./fields/checkbox-field";
import { SwitchField } from "./fields/switch-field";
import { RadioField } from "./fields/radio-field";
import { MoneyField } from "./fields/money-field";
import { PercentField } from "./fields/percent-field";
import { DateField } from "./fields/date-field";
import { SelectField } from "./fields/select-field";
import { MultiSelectField } from "./fields/multi-select-field";
import { TagsField } from "./fields/tags-field";
import { FileField } from "./fields/file-field";
import { RepeaterField } from "./fields/repeater-field";
import { RelationField } from "./fields/relation-field";

export interface FieldRendererProps {
  /** The field definition to render. Discriminated by `type`. */
  field: FieldDef;
  /**
   * Optional dotted path prefix used by {@link RepeaterField} to scope
   * nested fields under their row index, e.g. `"partners.0"`. When set,
   * field components register against `${pathPrefix}.${field.name}`
   * instead of just `field.name`, and resolve their error message from
   * the same nested path.
   */
  pathPrefix?: string;
}

export function FieldRenderer({
  field,
  pathPrefix,
}: FieldRendererProps): React.ReactElement | null {
  const { form } = useFormContext();
  // Subscribe to the full values map so any change re-runs the
  // conditional check. We always call `useWatch` — calling hooks
  // conditionally violates rules-of-hooks. When the field has no
  // `condition` predicate, the watched value is read but ignored.
  //
  // `useWatch` is functionally a subscription to rhf's internal value
  // tree. With no `name` argument it watches the whole form, which is
  // what we need here since the predicate is opaque to us — it could
  // depend on any field. Per-field predicates that only read one value
  // could declare a `dependencies` array in the future to narrow the
  // subscription, but the closed-form pattern today doesn't expose that.
  const watched = useWatch({ control: form.control });
  // Cast to the FormValues shape for the predicate. `useWatch` returns
  // `Partial<TValues>`; the predicate operates on a generic
  // `Record<string, unknown>` so the cast is safe and cheap.
  if (!evaluateCondition(field, (watched ?? {}) as FormValues)) {
    return null;
  }
  // The conditional spread keeps `exactOptionalPropertyTypes: true` happy:
  // each child accepts `pathPrefix?: string` and rejects the explicit
  // `undefined` value that destructuring would otherwise re-emit.
  const prefixProps: { pathPrefix?: string } =
    pathPrefix !== undefined ? { pathPrefix } : {};
  switch (field.type) {
    case "text":
      return <TextField field={field} {...prefixProps} />;
    case "textarea":
      return <TextareaField field={field} {...prefixProps} />;
    case "number":
      return <NumberField field={field} {...prefixProps} />;
    case "checkbox":
      return <CheckboxField field={field} {...prefixProps} />;
    case "switch":
      return <SwitchField field={field} {...prefixProps} />;
    case "radio":
      return <RadioField field={field} {...prefixProps} />;
    case "money":
      return <MoneyField field={field} {...prefixProps} />;
    case "percent":
      return <PercentField field={field} {...prefixProps} />;
    case "date":
      return <DateField field={field} {...prefixProps} />;
    case "select":
      return <SelectField field={field} {...prefixProps} />;
    case "multiSelect":
      return <MultiSelectField field={field} {...prefixProps} />;
    case "tags":
      return <TagsField field={field} {...prefixProps} />;
    case "file":
      return <FileField field={field} {...prefixProps} />;
    case "repeater":
      return <RepeaterField field={field} {...prefixProps} />;
    case "relation":
      return <RelationField field={field} {...prefixProps} />;
    default: {
      // The compile-time exhaustiveness check: if a new variant is added
      // to FieldType without a case here, `_exhaustive` will be typed as
      // the new variant rather than `never`, failing the typecheck.
      const _exhaustive: never = field;
      throw new Error(
        `Field type not yet implemented: ${(_exhaustive as { type: string }).type}`,
      );
    }
  }
}
